import React, { useContext } from "react";
import PropTypes from "prop-types";
import Chart from "react-apexcharts";
import { ThemeContext } from "../layout/themeContext";

// One measure of the tenant's report, day by day: a single area series, so the
// card's title names it and there is no legend. Two measures are two charts,
// never two axes. The report's day table has every value without hovering.
// Colors are purple-600 on white and purple-500 on gray-800, both checked for
// contrast against their surface.
const THEMES = {
  light: { series: "#7C3AED", surface: "#FFFFFF", grid: "#E5E7EB", text: "#6B7280" },
  dark: { series: "#8B5CF6", surface: "#1F2937", grid: "#374151", text: "#9CA3AF" },
};

const TenantTrendChart = ({ days, field, name, format }) => {
  const { theme } = useContext(ThemeContext);
  const colors = THEMES[theme === "dark" ? "dark" : "light"];

  const options = {
    chart: {
      type: "area",
      background: "transparent",
      fontFamily: "Inter, sans-serif",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    theme: { mode: theme === "dark" ? "dark" : "light" },
    colors: [colors.series],
    stroke: { width: 2, curve: "straight", lineCap: "round" },
    fill: { type: "solid", opacity: 0.1 },
    markers: {
      size: 0,
      strokeColors: colors.surface,
      strokeWidth: 2,
      hover: { size: 5 },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: {
      borderColor: colors.grid,
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      padding: { left: 8, right: 8 },
    },
    xaxis: {
      categories: days.map((day) => day.day),
      tickAmount: Math.min(days.length - 1, 8),
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
        formatter: (value) => String(value || "").slice(5),
        style: { colors: colors.text },
      },
      axisBorder: { color: colors.grid },
      axisTicks: { show: false },
      crosshairs: { show: true, stroke: { color: colors.text, width: 1, dashArray: 0 } },
      tooltip: { enabled: false },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: (value) =>
          new Intl.NumberFormat("mn-MN", { notation: "compact" }).format(value),
        style: { colors: colors.text },
      },
    },
    tooltip: {
      theme: theme === "dark" ? "dark" : "light",
      x: { formatter: (value, { dataPointIndex }) => days[dataPointIndex]?.day || value },
      y: { formatter: format },
    },
    // Fewer days on the axis of a phone, so they do not run into each other
    responsive: [
      { breakpoint: 640, options: { xaxis: { tickAmount: Math.min(days.length - 1, 4) } } },
    ],
  };

  const series = [{ name, data: days.map((day) => day[field]) }];

  return <Chart options={options} series={series} type="area" height={240} />;
};

TenantTrendChart.propTypes = {
  days: PropTypes.array.isRequired,
  field: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  format: PropTypes.func.isRequired,
};

export default TenantTrendChart;
